import { Box, Button, Chip, Typography } from '@mui/material';
import DOMPurify from 'dompurify';
import type React from 'react';
import { useNavigate } from 'react-router-dom';
import { BaseModal } from '../../components/common/UI/BaseModal';
import type { Assignment } from '../../types/assignments';

interface AssignmentDetailsProps {
  open: boolean;
  onClose: () => void;
  assignment: Assignment | null;
}

const getStatusColor = (
  status?: string
): 'success' | 'warning' | 'info' | 'default' | 'primary' | 'secondary' | 'error' => {
  switch (status?.toLowerCase()) {
    case 'completed':
    case 'approved':
    case 'done':
      return 'success';
    case 'in_progress':
    case 'in progress':
    case 'drafting':
      return 'warning';
    case 'review':
    case 'in_review':
      return 'info';
    case 'not_started':
    case 'not started':
    case 'pending':
      return 'default';
    default:
      return 'default';
  }
};

const AssignmentDetails: React.FC<AssignmentDetailsProps> = ({ open, onClose, assignment }) => {
  const navigate = useNavigate();

  if (!assignment) {
    return null;
  }

  const handleGoToTender = () => {
    onClose();
    navigate(`/tender/${assignment.tenderId}`);
  };

  const handleGoToSection = () => {
    onClose();
    navigate(`/tender/${assignment.tenderId}?step=7&actionSectionId=${assignment.sectionId}`);
  };

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      title={
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" component="div" fontWeight="bold">
              {assignment.sectionTitle || 'Untitled Section'}
            </Typography>
            <Chip
              label={(assignment.status || 'Pending').replace(/_/g, ' ')}
              size="small"
              color={getStatusColor(assignment.status)}
              variant="outlined"
              sx={{ textTransform: 'capitalize' }}
            />
          </Box>
          <Typography variant="subtitle2" component="div" color="text.secondary">
            {assignment.tenderName || 'Unknown Tender'}
          </Typography>
        </Box>
      }
      actions={
        <>
          <Button onClick={onClose} color="inherit">
            Close
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button variant="outlined" onClick={handleGoToTender}>
            Go to Tender
          </Button>
          <Button variant="contained" color="primary" onClick={handleGoToSection}>
            Go to Section
          </Button>
        </>
      }
    >
      <Box sx={{ mb: 2, mt: 1 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Assigned To
        </Typography>
        <Typography variant="body2">{assignment.assignee?.displayName || 'Unassigned'}</Typography>
      </Box>
      <Box>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Section Text
        </Typography>
        <Box
          sx={{
            p: 2,
            backgroundColor: 'action.hover',
            borderRadius: 1,
            minHeight: 100,
            maxHeight: 400,
            overflowY: 'auto',
            '& p': { m: 0, mb: 1 },
            '& ul, & ol': { m: 0, pl: 2, mb: 1 },
          }}
        >
          {assignment.sectionText ? (
            <Box
              sx={{ typography: 'body2', wordBreak: 'break-word' }}
              // biome-ignore lint/security/noDangerouslySetInnerHtml: Content is sanitized
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(assignment.sectionText) }}
            />
          ) : (
            <Typography variant="body2" color="text.disabled" fontStyle="italic">
              No text available for this section.
            </Typography>
          )}
        </Box>
      </Box>
    </BaseModal>
  );
};

export default AssignmentDetails;
